import { bffServerAddress } from '../common/MetaSetting';
import { offlineCryptoStorage } from './OfflineCryptoStorage';
import { addCsrfToHeaders, extractCsrfTokenFromResponse } from '../utils/csrf';

const CLIENT_ID = 'memo-client-secret';
const BFF_API_BASE = `${bffServerAddress}/api/bff`;
const EXPLICIT_LOGOUT_KEY = 'bff_explicit_logout';

interface UserInfo {
  sub: string;
  email?: string;
  name?: string;
  picture?: string;
  [key: string]: any;
}

class BffAuthService {
  private userInfo: UserInfo | null = null;
  private authenticated: boolean = false;
  private listeners: Array<(authenticated: boolean) => void> = [];
  private initialized: boolean = false;
  private initPromise: Promise<boolean> | null = null;

  constructor() {
    this.loadUserInfoFromStorage();
  }

  subscribe(listener: (authenticated: boolean) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(authenticated: boolean): void {
    this.listeners.forEach(listener => listener(authenticated));
  }

  private loadUserInfoFromStorage(): void {
    try {
      const userStr = sessionStorage.getItem('bff_userinfo');
      if (userStr) {
        this.userInfo = JSON.parse(userStr);
      }
    } catch (e) {
      // ignore
    }
  }

  private saveUserInfoToStorage(): void {
    if (this.userInfo) {
      sessionStorage.setItem('bff_userinfo', JSON.stringify(this.userInfo));
    } else {
      sessionStorage.removeItem('bff_userinfo');
    }
  }

  get isAuthenticated(): boolean {
    return this.authenticated;
  }

  getUserInfo(): UserInfo | null {
    return this.userInfo;
  }

  isExplicitlyLoggedOut(): boolean {
    return sessionStorage.getItem(EXPLICIT_LOGOUT_KEY) === 'true';
  }

  async initSession(): Promise<boolean> {
    if (this.initialized) {
      return this.authenticated;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.doInitSession();
    return this.initPromise;
  }

  private async doInitSession(): Promise<boolean> {
    if (this.isExplicitlyLoggedOut()) {
      this.authenticated = false;
      this.initialized = true;
      this.notifyListeners(false);
      return false;
    }

    try {
      const response = await this.fetchWithCsrf(`${BFF_API_BASE}/${CLIENT_ID}/session/status`, {
        method: 'GET'
      });

      if (!response.ok) {
        this.authenticated = false;
        this.initialized = true;
        this.notifyListeners(false);
        return false;
      }

      const data = await response.json();
      this.authenticated = data.authenticated === true;
      this.initialized = true;

      if (this.authenticated) {
        const wasUnauthenticated = !this.userInfo;
        if (wasUnauthenticated) {
          await offlineCryptoStorage.clearAll();
          console.info('[BffAuthService] Re-authenticated, IndexedDB cleared');
        }
        await this.fetchUserInfo();
      }

      this.notifyListeners(this.authenticated);
      return this.authenticated;
    } catch (error) {
      this.authenticated = false;
      this.initialized = true;
      this.notifyListeners(false);
      return false;
    }
  }

  login(): void {
    sessionStorage.removeItem(EXPLICIT_LOGOUT_KEY);
    window.location.href = `${BFF_API_BASE}/${CLIENT_ID}/authorize`;
  }

  async fetchUserInfo(): Promise<UserInfo | null> {
    const maxRetries = 3;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await this.fetchWithCsrf(`${BFF_API_BASE}/${CLIENT_ID}/userinfo`, {
          method: 'GET'
        });

        if (!response.ok) {
          if (response.status === 401) {
            this.authenticated = false;
            this.notifyListeners(false);
            return null;
          }
          if (response.status === 429) {
            console.warn('[BffAuthService] Rate limited on userinfo fetch, retrying...');
            await new Promise(resolve => setTimeout(resolve, 2000 * (attempt + 1)));
            continue;
          }
          return null;
        }

        const data = await response.json();
        this.userInfo = data;
        this.saveUserInfoToStorage();
        return this.userInfo;
      } catch (error) {
        return null;
      }
    }
    return null;
  }

  private cachedToken: string | null = null;
  private tokenExpiry: number = 0;
  private static TOKEN_CACHE_BUFFER_MS = 30 * 1000; // 提前 30 秒过期，留出缓冲
  private tokenFetchPromise: Promise<string | null> | null = null;

  async getAccessToken(): Promise<string | null> {
    if (this.cachedToken && Date.now() < this.tokenExpiry) {
      return this.cachedToken;
    }

    // 并发去重：多个调用共享同一个 promise
    if (this.tokenFetchPromise) {
      return this.tokenFetchPromise;
    }

    this.tokenFetchPromise = this._fetchAccessToken();
    try {
      return await this.tokenFetchPromise;
    } finally {
      this.tokenFetchPromise = null;
    }
  }

  private async _fetchAccessToken(): Promise<string | null> {
    const maxRetries = 3;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await this.fetchWithCsrf(`${BFF_API_BASE}/${CLIENT_ID}/token`, {
          method: 'GET'
        });

        if (!response.ok) {
          if (response.status === 429) {
            console.warn('[BffAuthService] Rate limited on token fetch, retrying...');
            await new Promise(resolve => setTimeout(resolve, 2000 * (attempt + 1)));
            continue;
          }
          return null;
        }

        const data = await response.json();
        const token = data.access_token || null;
        if (token) {
          this.cachedToken = token;
          // 使用服务器返回的 expires_in 计算缓存过期时间，提前 30 秒过期
          const expiresIn = data.expires_in || 300;
          this.tokenExpiry = Date.now() + Math.max(expiresIn * 1000 - BffAuthService.TOKEN_CACHE_BUFFER_MS, 0);
        }
        return token;
      } catch (error) {
        return null;
      }
    }
    return null;
  }

  async logout(): Promise<void> {
    try {
      sessionStorage.setItem(EXPLICIT_LOGOUT_KEY, 'true');
      const response = await this.fetchWithCsrf(`${BFF_API_BASE}/${CLIENT_ID}/logout`, {
        method: 'POST'
      });

      await this.clearAuthState();
      this.notifyListeners(false);

      if (response.ok) {
        const data = await response.json();
        if (data.end_session_url) {
          window.location.href = data.end_session_url;
          return;
        }
      }
    } catch (error) {
      sessionStorage.setItem(EXPLICIT_LOGOUT_KEY, 'true');
      await this.clearAuthState();
      this.notifyListeners(false);
    }
  }

  async clearAuthState(): Promise<void> {
    this.authenticated = false;
    this.userInfo = null;
    this.initialized = false;
    this.initPromise = null;
    this.cachedToken = null;
    this.tokenExpiry = 0;
    sessionStorage.removeItem('bff_userinfo');

    try {
      await offlineCryptoStorage.clearAll();
    } catch (e) {
      console.error('[BffAuthService] Failed to clear offline storage on logout:', e);
    }
  }

  handleAuthLost(): void {
    if (!this.authenticated && this.initialized) {
      return;
    }
    this.authenticated = false;
    this.userInfo = null;
    this.initialized = true;
    this.initPromise = null;
    this.cachedToken = null;
    this.tokenExpiry = 0;
    sessionStorage.removeItem('bff_userinfo');
    this.notifyListeners(false);

    offlineCryptoStorage.clearAll().catch(e => {
      console.error('[BffAuthService] Failed to clear offline storage on auth lost:', e);
    });
  }

  private silentReauthPromise: Promise<boolean> | null = null;

  /**
   * 尝试静默恢复认证状态：
   * 1. 先检查 BFF Session 是否有效
   * 2. 如果 Session 有效，直接恢复
   * 3. 如果 Session 检查返回未认证，尝试通过 /token 端点获取 access_token
   *    （BFF 的 /token 端点使用 TokenRefreshService，会自动用 refresh_token 刷新，
   *    token 存储在 Redis 中，与 session 生命周期解耦）
   * 4. 如果都失败，标记认证丢失
   */
  async trySilentReauth(): Promise<boolean> {
    // 防止并发调用
    if (this.silentReauthPromise) {
      return this.silentReauthPromise;
    }

    this.silentReauthPromise = this._doSilentReauth();
    try {
      return await this.silentReauthPromise;
    } finally {
      this.silentReauthPromise = null;
    }
  }

  private async _doSilentReauth(): Promise<boolean> {
    try {
      // 第一步：检查 BFF Session 状态
      const sessionResponse = await this.fetchWithCsrf(`${BFF_API_BASE}/${CLIENT_ID}/session/status`, {
        method: 'GET'
      });

      if (sessionResponse.ok) {
        const data = await sessionResponse.json();
        if (data.authenticated === true) {
          this.authenticated = true;
          this.initialized = true;
          this.cachedToken = null;
          this.tokenExpiry = 0;
          this.notifyListeners(true);
          console.info('[BffAuthService] Silent re-auth succeeded, session still valid');
          return true;
        }
      }

      // 第二步：Session 状态返回未认证，尝试直接获取 token
      // BFF 的 /token 端点使用 TokenRefreshService，会自动用 refresh_token 刷新
      // token 存储在 Redis 中，与 session 生命周期解耦
      console.info('[BffAuthService] Session status says unauthenticated, trying /token endpoint');
      const tokenResponse = await this.fetchWithCsrf(`${BFF_API_BASE}/${CLIENT_ID}/token`, {
        method: 'GET'
      });

      if (tokenResponse.ok) {
        const tokenData = await tokenResponse.json();
        if (tokenData.access_token) {
          this.authenticated = true;
          this.initialized = true;
          this.cachedToken = tokenData.access_token;
          const expiresIn = tokenData.expires_in || 300;
          this.tokenExpiry = Date.now() + Math.max(expiresIn * 1000 - BffAuthService.TOKEN_CACHE_BUFFER_MS, 0);
          await this.fetchUserInfo();
          this.notifyListeners(true);
          console.info('[BffAuthService] Silent re-auth succeeded via /token endpoint');
          return true;
        }
      }

      // 第三步：都失败，标记认证丢失
      console.info('[BffAuthService] Silent re-auth failed, auth lost');
      this.handleAuthLost();
      return false;
    } catch (error) {
      console.warn('[BffAuthService] Silent re-auth check failed:', error);
      this.handleAuthLost();
      return false;
    }
  }

  async checkSessionStatus(): Promise<boolean> {
    try {
      const response = await this.fetchWithCsrf(`${BFF_API_BASE}/${CLIENT_ID}/session/status`, {
        method: 'GET'
      });

      if (!response.ok) {
        if (response.status === 429) {
          console.warn('[BffAuthService] Rate limited on session status check, returning cached state');
          return this.authenticated;
        }
        return false;
      }

      const data = await response.json();
      const wasAuthenticated = this.authenticated;
      this.authenticated = data.authenticated === true;

      if (wasAuthenticated !== this.authenticated) {
        this.notifyListeners(this.authenticated);
      }

      return this.authenticated;
    } catch (error) {
      return false;
    }
  }

  private async fetchWithCsrf(url: string, options: RequestInit): Promise<Response> {
    const headers: Record<string, string> = {};
    
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    
    addCsrfToHeaders(headers);

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    });

    extractCsrfTokenFromResponse(response);

    return response;
  }
}

export const bffAuthService = new BffAuthService();
export type { UserInfo };
